import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useWebsite } from '../../../hooks/useWebsite';
import { useDateRange } from '../../../hooks/useDateRange';

interface GenericTopListProps<T> {
  itemsProvider: (websiteId: string, args: { from: Date; to: Date, timezone: string }) => Promise<T[]>;
  maxItems?: number;
  title: string;
  getKey: (item: T) => string;
  getLabel: (item: T) => React.ReactNode;
  getValue: (item: T) => number;
  className?: string;
}

export const BaseCard = <T,>(props: GenericTopListProps<T>): JSX.Element => {
  const { websiteId } = useWebsite();
  const { from, to, timezone } = useDateRange();
  const { itemsProvider, maxItems = 10, title, getKey, getLabel, getValue, className } = props;
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await itemsProvider(websiteId, { from, to, timezone });
        setItems(data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [from, to, itemsProvider, websiteId]);

  if (loading) {
    return (
      <div className="card h-100">
        <div className="card-header"><div className="card-title">{title}</div></div>
        <div className="card-body">Loading…</div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="card">
        <div className="card-header"><div className="card-title">{title}</div></div>
        <div className="card-body">No data available.</div>
      </div>
    );
  }

  const total = items.reduce((s, i) => s + getValue(i), 0);
  const displayed = items.slice(0, maxItems);
  const others = items.slice(maxItems);
  const othersValue = others.reduce((s, i) => s + getValue(i), 0);
  const othersPercent = total > 0 ? ((othersValue / total) * 100).toFixed(1) : '0.0';

  return (
    <div className={clsx('card', className)}>
      <div className="card-header"><div className="card-title">{title}</div></div>
      <div className="card-body">
        <div className="d-flex flex-column gap-2">
          {displayed.map((item) => {
            const value = getValue(item);
            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return (
              <div key={getKey(item)} className="d-flex justify-content-between align-items-center">
                <div className="text-truncate fw-bold text-gray-600 me-3">
                  {getLabel(item)}
                </div>
                <div className="d-flex align-items-center flex-shrink-0">
                  <span className="fw-bold text-end" style={{ minWidth: '60px' }}>{value}</span>
                  <span className="text-gray-600 text-end" style={{ minWidth: '50px' }}>{percent}%</span>
                </div>
              </div>
            );
          })}
          {othersValue > 0 && (
            <div key="others" className="d-flex justify-content-between align-items-center">
              <div className="fw-bold text-gray-600 me-3">Others {others.length}</div>
              <div className="d-flex align-items-center flex-shrink-0">
                <span className="fw-bold text-end" style={{ minWidth: '60px' }}>{othersValue}</span>
                <span className="text-gray-600 text-end" style={{ minWidth: '50px' }}>{othersPercent}%</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
